import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const BLOCK = 'LStudio__app_popover';

/*
    🔥 POPOVER GENÉRICO

    La base de la que deberían salir UtilityDropdown y TableActionMenu:
    hoy cada uno resuelve por su cuenta el posicionado, el cierre y el
    teclado, con criterios distintos.

    ⚠️ VA POR PORTAL, NO DENTRO DEL PADRE

    Un menú dentro de su contenedor lo recorta cualquier ancestro con
    overflow: hidden — y una tabla con scroll horizontal SIEMPRE tiene
    uno. Por eso sale al <body> y se posiciona en coordenadas de
    ventana. Es también el motivo de --LS__z_float en la escala.

    ⚠️ SE REUBICA SOLO SI NO ENTRA

    Con placement="bottom" pero sin lugar abajo, se da vuelta hacia
    arriba en vez de quedar cortado contra el borde. Lo mismo en el eje
    horizontal.

    ⚠️ CIERRA CON SCROLL, NO SE ARRASTRA

    Si la página scrollea, el popover NO persigue al disparador: se
    cierra. Perseguir obliga a recalcular en cada frame y produce un
    arrastre visible. Cerrar es lo que hacen los menús nativos.
*/

const GAP = 8;
const MARGIN = 12;

const onComputePosition = (anchorRect, panelRect, placement, align) => {

    const VW = window.innerWidth;
    const VH = window.innerHeight;

    // -------------------- 🎯 EJE PRINCIPAL
    let side = placement;

    const SPACE_BELOW = VH - anchorRect.bottom;
    const SPACE_ABOVE = anchorRect.top;

    if (side === 'bottom' && SPACE_BELOW < panelRect.height + GAP + MARGIN && SPACE_ABOVE > SPACE_BELOW) {
        side = 'top';
    }

    if (side === 'top' && SPACE_ABOVE < panelRect.height + GAP + MARGIN && SPACE_BELOW > SPACE_ABOVE) {
        side = 'bottom';
    }

    const TOP = side === 'top'
        ? anchorRect.top - panelRect.height - GAP
        : anchorRect.bottom + GAP;

    // -------------------- 🎯 EJE SECUNDARIO
    let left = anchorRect.left;

    if (align === 'center') left = anchorRect.left + (anchorRect.width - panelRect.width) / 2;

    if (align === 'end') left = anchorRect.right - panelRect.width;

    // 🔥 Nunca fuera de la ventana, pase lo que pase con el align
    left = Math.min(Math.max(MARGIN, left), VW - panelRect.width - MARGIN);

    return { top: Math.max(MARGIN, TOP), left, side };
};

export const AppPopover = ({
    isOpen,
    onClose,
    anchorRef,
    children,
    placement = 'bottom',        // bottom | top
    align = 'start',             // start | center | end
    width = null,                // número en px, o null para el ancho del contenido
    matchAnchorWidth = false,    // el panel copia el ancho del disparador (selects)
    isDismissible = true,
    className = '',
    role = 'dialog',             // dialog | menu | listbox
}) => {

    const REF__useRefPanel = useRef(null);

    const [usePosition, setPosition] = useState({ top: -9999, left: -9999, side: placement });

    /*
        ⚠️ useLayoutEffect Y NO useEffect

        Con useEffect el panel se pinta un frame en (0,0) y salta a su
        lugar. Es un parpadeo corto pero muy visible en un menú que se
        abre y cierra seguido.
    */
    useLayoutEffect(() => {

        if (!isOpen || !anchorRef?.current || !REF__useRefPanel.current) return;

        const ANCHOR = anchorRef.current.getBoundingClientRect();
        const PANEL = REF__useRefPanel.current.getBoundingClientRect();

        setPosition(onComputePosition(ANCHOR, PANEL, placement, align));

    }, [isOpen, placement, align, anchorRef, children]);

    // -------------------- 🎯 CIERRE
    useEffect(() => {

        if (!isOpen) return;

        const onPointerDown = (event) => {
            if (!isDismissible) return;
            if (REF__useRefPanel.current?.contains(event.target)) return;
            if (anchorRef?.current?.contains(event.target)) return;
            onClose?.();
        };

        const onKeyDown = (event) => {
            if (event.key !== 'Escape') return;
            onClose?.();
            // 🔥 El foco vuelve al disparador: si no, se va al <body>
            anchorRef?.current?.focus?.();
        };

        const onScrollOrResize = () => onClose?.();

        document.addEventListener('mousedown', onPointerDown);
        document.addEventListener('keydown', onKeyDown);
        window.addEventListener('resize', onScrollOrResize);
        // 🔥 true = fase de captura: atrapa el scroll de CUALQUIER contenedor,
        //    no solo el de la ventana.
        window.addEventListener('scroll', onScrollOrResize, true);

        return () => {
            document.removeEventListener('mousedown', onPointerDown);
            document.removeEventListener('keydown', onKeyDown);
            window.removeEventListener('resize', onScrollOrResize);
            window.removeEventListener('scroll', onScrollOrResize, true);
        };

    }, [isOpen, isDismissible, onClose, anchorRef]);

    if (!isOpen) return null;

    const ANCHOR_WIDTH = matchAnchorWidth
        ? anchorRef?.current?.getBoundingClientRect().width
        : null;

    return createPortal(
        <div
            ref={REF__useRefPanel}
            className={`${BLOCK} ${className}`}
            role={role}
            data-side={usePosition.side}
            style={{
                top: usePosition.top,
                left: usePosition.left,
                width: ANCHOR_WIDTH ?? width ?? undefined,
            }}
        >
            {children}
        </div>,
        document.body,
    );
};

/*
    🔥 PIEZAS PARA ARMAR MENÚS

    Sirven para que dos menús de dos módulos distintos no terminen con
    padding y tipografía diferentes.
*/
export const PopoverItem = ({
    icon = null, tone = 'neutral', isDisabled = false, onClick, children,
}) => (
    <button type="button" className={`${BLOCK}__item`} data-tone={tone}
        disabled={isDisabled} onClick={onClick} role="menuitem" >

        {icon && <span className={`${BLOCK}__item_icon`} aria-hidden="true">{icon}</span>}

        <span className={`${BLOCK}__item_label`}>{children}</span>

    </button>
);

export const PopoverDivider = () => <span className={`${BLOCK}__divider`} role="separator" />;

export const PopoverTitle = ({ children }) => (
    <p className={`${BLOCK}__title`}>{children}</p>
);
